"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Gift,
  Star,
  Calendar,
  ExternalLink,
  Clock,
  CheckCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface UpdateItem {
  icon: React.ComponentType<{ className?: string }>;
  text: string;
}

interface TimelineItem {
  date: string;
  month: string;
  text: string;
}

interface UpdatesMenuProps {
  className?: string;
  title?: string;
  version?: string;
  lastUpdated?: string;
  whatsNew?: UpdateItem[];
  timeline?: TimelineItem[];
  viewAllHref?: string;
  featuredImage?: string;
  featuredTitle?: string;
  featuredDescription?: string;
  featuredButtonText?: string;
  featuredButtonHref?: string;
}

const defaultWhatsNew: UpdateItem[] = [
  { icon: Star, text: "Stripe billing: subscribe, manage plan, and payment history" },
  { icon: Star, text: "Game search, batch actions, and delete with confirmation" },
  { icon: Star, text: "Dashboard layouts with training zone panel" },
  { icon: Star, text: "Multi-sport support: baseball mode with filtering" },
  { icon: Star, text: "In-app documentation with chapter navigation" },
  { icon: Star, text: "Monitoring and observability commands" },
];

const defaultTimeline: TimelineItem[] = [
  { date: "17", month: "Feb", text: "Games: search, batch actions, delete; dashboard layouts" },
  { date: "16", month: "Feb", text: "Stripe billing, subscriptions, multi-sport plans" },
  { date: "15", month: "Feb", text: "Docs section, domain handling, Tailwind v4 fixes" },
  { date: "14", month: "Feb", text: "Onboarding improvements, metro demo data, Stripe setup" },
  { date: "09", month: "Feb", text: "Mileage batch actions, dev warmup, fast dev workflow" },
  { date: "04", month: "Feb", text: "Partners, teams, mileage, orgs -- BUILDPROMPT Feb batch" },
];

export function UpdatesMenu({
  className,
  title = "Officials Pay",
  version = "1.4.0",
  lastUpdated = "Feb 18, 2026",
  whatsNew = defaultWhatsNew,
  timeline = defaultTimeline,
  viewAllHref = "/changelog",
  featuredImage = "/images/featured-update.jpg",
  featuredTitle = "Stripe billing and multi-sport support",
  featuredDescription = "Subscribe and manage your plan with Stripe. Baseball sport mode with position filtering. Game search, batch actions, and delete. Dashboard layouts with training zone.",
  featuredButtonText = "View Changelog",
  featuredButtonHref = "/changelog",
}: UpdatesMenuProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn("h-8 w-8", className)}
          title="Updates & Changelog"
        >
          <Gift className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm">{title}</h3>
            <Badge variant="secondary" className="text-xs">
              {version}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Last updated {lastUpdated}
          </p>
        </div>

        <Separator />

        {/* Featured Content - Minimal */}
        <div className="p-4 pb-2">
          <div className="space-y-2">
            <h4 className="font-medium text-sm">{featuredTitle}</h4>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {featuredDescription}
            </p>
            <Button
              size="sm"
              className="h-7 text-xs"
              onClick={() => {
                setOpen(false);
                window.location.href = featuredButtonHref;
              }}
            >
              {featuredButtonText}
            </Button>
          </div>
        </div>

        <Separator />

        {/* What's New Section */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Star className="h-4 w-4 text-yellow-500" />
            <h4 className="font-medium text-sm">What's New</h4>
          </div>
          <ScrollArea className="h-32">
            <div className="space-y-2">
              {whatsNew.map((item, index) => (
                <div key={index} className="flex items-start gap-2">
                  <item.icon className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
                  <span className="text-xs text-muted-foreground">
                    {item.text}
                  </span>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        <Separator />

        {/* Timeline Section */}
        <div className="p-3">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="h-4 w-4 text-blue-500" />
            <h4 className="font-medium text-sm">Recent Updates</h4>
          </div>
          <ScrollArea className="h-32">
            <div className="space-y-1">
              {timeline.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <span className="text-xs font-medium">{item.date}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground truncate">
                      {item.text}
                    </p>
                    {item.month && (
                      <p className="text-xs text-muted-foreground/70">
                        {item.month}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        <Separator />

        {/* Footer */}
        <div className="p-4">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 h-7 text-xs"
              onClick={() => {
                setOpen(false);
                window.location.href = "/changelog";
              }}
            >
              <ExternalLink className="h-3 w-3 mr-1" />
              Changelog
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="flex-1 h-7 text-xs"
              onClick={() => {
                setOpen(false);
                window.location.href = "/changes";
              }}
            >
              Changes
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
